import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import StorageMeshGrid from '@/components/StorageMeshGrid';
import GridScenarioVideos from '@/components/GridScenarioVideos';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import NetzdienlichStempel from '@/components/NetzdienlichStempel';

const HYPER_MESH_URL = 'https://www.hyper-mesh.de';

// Illustrative Trafo-Auslastung: steigt periodisch in den Überlastbereich (externe Netzlast).
// Keine echten Messdaten.
function useSimulatedTrafoLoad() {
  const [load, setLoad] = useState(0.55);
  useEffect(() => {
    const t0 = Date.now();
    const id = setInterval(() => {
      const s = (Date.now() - t0) / 1000;
      const v = 0.56 + 0.3 * Math.sin((2 * Math.PI * s) / 24) + 0.04 * Math.sin(s * 1.3);
      setLoad(Math.min(0.98, Math.max(0.2, v)));
    }, 400);
    return () => clearInterval(id);
  }, []);
  return load;
}

const HyperGrid = () => {
  const { t } = useTranslation();
  const trafoLoad = useSimulatedTrafoLoad();
  const [faultCount, setFaultCount] = useState(0);
  const [showScenario, setShowScenario] = useState(false);
  const overload = trafoLoad > 0.8;
  return (
    <div className="animate-fadeIn bg-slate-900 min-h-screen text-white relative" data-testid="hyper-grid-page">
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
        <LanguageSwitcher />
      </div>
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center text-slate-400 hover:text-white mb-8 text-sm">
            <ArrowLeft className="mr-2" size={16} />
            {t('hyperGrid.backToHome')}
          </Link>

          <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
            {t('hyperGrid.titlePrefix')}{' '}
            <span className="bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
              HYPER GRID
            </span>
          </h1>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            {t('hyperGrid.subtitle')}
          </p>

          <div className="relative max-w-5xl mx-auto mb-10 rounded-2xl border border-green-500/40 bg-green-500/10 p-6 text-left md:p-8" data-testid="grid-operator-block">
            <NetzdienlichStempel className="absolute right-4 top-4 z-10 md:right-8 md:top-6" size="lg" delay={0.4} />
            <span className="btn-vibrate mb-3 inline-block rounded-full bg-green-500 px-4 py-1 text-sm font-bold uppercase tracking-wide text-slate-900">
              {t('hyperGrid.operatorBadge')}
            </span>
            <h2 className="mb-3 pr-0 text-2xl font-bold sm:pr-72 md:text-3xl">{t('hyperGrid.operatorTitle')}</h2>
            <p className="mb-6 max-w-3xl text-slate-200 sm:pr-64">{t('hyperGrid.operatorLead')}</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="rounded-xl border border-slate-700 bg-slate-800/70 p-4">
                  <h3 className="mb-1 font-semibold text-green-300">{t(`hyperGrid.operatorP${n}Title`)}</h3>
                  <p className="text-sm text-slate-300">{t(`hyperGrid.operatorP${n}Text`)}</p>
                </div>
              ))}
            </div>
          </div>

          <div
            className="max-w-5xl mx-auto mb-4 px-4 py-3 rounded-xl border border-slate-700 bg-slate-800/60 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-left"
            data-testid="hyper-grid-monitor"
            aria-live="polite"
          >
            <div>
              <span className="font-semibold mr-2">{t('hyperGrid.monitorTitle')}</span>
              <span className={faultCount ? 'text-red-400' : 'text-green-400'}>
                {faultCount ? t('hyperGrid.monitorFaults', { count: faultCount }) : t('hyperGrid.monitorOk')}
              </span>
            </div>
            <div className={overload ? 'text-red-400 font-semibold' : 'text-green-400'}>
              {t('hyperGrid.trafoLoad')} {Math.round(trafoLoad * 100)} % ·{' '}
              {overload ? t('hyperGrid.trafoOverload') : t('hyperGrid.trafoNormal')}
            </div>
          </div>

          <div className="max-w-5xl mx-auto mb-4">
            <StorageMeshGrid
              seed={1}
              mode="investor"
              callouts={{
                1: { text: 'Investor 1', offset: [110, -60] },
                47: { text: 'Investor 47', offset: [150, -10] },
                100: { text: 'Investor 100', offset: [-160, 40] },
              }}
              caption={t('hyperGrid.caption')}
              trafoLabel={t('hyperGrid.trafoLabel')}
              trafoLoad={trafoLoad}
              onTrafoClick={() => setShowScenario(true)}
              trafoAriaLabel={t('hyperGrid.trafoClick')}
              onFaultCount={setFaultCount}
            />
          </div>
          <p className="text-sm text-slate-400 mb-10">{t('hyperGrid.trafoClick')}</p>

          {showScenario && <GridScenarioVideos onClose={() => setShowScenario(false)} />}

          <a href={HYPER_MESH_URL} target="_blank" rel="noopener noreferrer">
            <button className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white font-semibold rounded-full transition-all duration-300">
              {t('hyperGrid.ctaButton')}
              <ExternalLink size={20} />
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default HyperGrid;
